/**
 * The ONE definition of "this provider has stated a location".
 *
 * It lives in its own leaf module because two different layers have to agree on
 * it and they sit on opposite sides of an import cycle: `scheduleEditorModel`
 * (the go-live guard) and `providersScheduleIntake` (the signup serializer),
 * where the serializer already imports the guard's module.
 *
 * WHY THEY HAVE TO AGREE. They did not, and it cost providers their entire
 * match deck, silently. `guardFor` accepted any non-empty string, so typing an
 * ambiguous city ("Charleston" -- SC or WV?) let the card go live looking
 * finished. `buildDreamScheduleV2` then asked THIS function whether the text was
 * usable, got null, and OMITTED `locationPreference` from the profile payload
 * rather than send something `updateProfile` would 400 on. The provider ended up
 * live, shareable, and excluded from every deck on geo -- by a matcher doing its
 * job on a location the builder had thrown away. Nothing surfaced the loss: the
 * card still rendered, and its "Location not specified (25 mi)" placeholder
 * looked like a field they had skipped rather than one that was discarded.
 *
 * Two callers, one meaning: the guard REFUSES go-live on anything this rejects,
 * and the serializer writes exactly what this returns.
 */

/**
 * ProviderProfile.locationPreference, or null when the text cannot become one.
 *
 * ERRS TOWARD NULL ON PURPOSE. `provider.service.ts` updateProfile throws
 * BadRequestException on a bare zip or a malformed "City, State", which would
 * 400 the whole profile patch and lose every other field with it. So this
 * mirrors that method's acceptance rule and rejects anything it would refuse.
 * A bare state would actually be expanded by the backend, but is rejected here
 * too: the wizard rejects it and the door never produces one.
 */
export function locationPreferenceFrom(locationText) {
  const text = String(locationText || '').trim();
  if (!text) return null;
  if (/^\d{5}(-\d{4})?$/.test(text)) return null;
  const parts = text.split(',').map((s) => s.trim());
  if (parts.length < 2 || parts[0].length < 2 || parts[1].length < 2) return null;
  return `${parts[0]}, ${parts[1]}`;
}

/**
 * Would this draft's location survive the trip into the profile?
 *
 * The predicate the go-live guard asks. Named separately from the converter so
 * a reader of `guardFor` sees a question, not a discarded return value.
 */
export function carriesToProfile(locationText) {
  return locationPreferenceFrom(locationText) !== null;
}
